import { format } from '@seek/currency';
import {
  getAbsoluteAllowableMaximum,
  getAbsoluteAllowableMinimum,
  getRelativeAllowableMaximum,
  getRelativeAllowableMinimum,
} from '@seek/salary-data';
import type {
  SalaryCurrency,
  SalaryFrequency,
} from '@seek/salary-data/lib-types/types';

import { LANGUAGE } from 'src/app/shared/constants/configObjectProperties';
import { LANGUAGE_LOCALE_DEFAULT } from 'src/app/shared/constants/language';
import { getConfig } from 'src/app/utils/config';

interface IGetMinMaxAndAllowablesArg {
  minSalary?: number | null;
  maxSalary?: number | null;
  validationType?: 'min' | 'max';
  currency: SalaryCurrency;
  frequency: SalaryFrequency;
  value?: string;
}

const locale = getConfig(LANGUAGE, LANGUAGE_LOCALE_DEFAULT);

export const getMinMaxAndAllowables = ({
  minSalary,
  maxSalary,
  validationType,
  currency,
  frequency,
  value,
}: IGetMinMaxAndAllowablesArg) => {
  const minimumText = validationType === 'min' ? value : minSalary?.toString();
  const maximumText = validationType === 'max' ? value : maxSalary?.toString();
  const minimum = Number(minimumText);
  const maximum = Number(maximumText);

  const allowableMin = getRelativeAllowableMinimum({
    maximum,
    currency,
    frequency,
  });

  const allowableMax = getRelativeAllowableMaximum({
    minimum,
    currency,
    frequency,
  });

  const minAllowableSalary = getAbsoluteAllowableMinimum({
    currency,
    frequency,
  });

  const maxAllowableSalary = getAbsoluteAllowableMaximum({
    currency,
    frequency,
  });

  const minAllowableSalaryText = format(minAllowableSalary, {
    currency,
    locale,
    dropFraction: true,
  });

  const maxAllowableSalaryText = format(maxAllowableSalary, {
    currency,
    locale,
    dropFraction: true,
  });

  return {
    minimum,
    minimumText,
    maximumText,
    maximum,
    allowableMax,
    allowableMin,
    minAllowableSalary,
    maxAllowableSalary,
    minAllowableSalaryText,
    maxAllowableSalaryText,
  };
};
export type GetMinMaxAndAllowablesResult = ReturnType<
  typeof getMinMaxAndAllowables
>;
